import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    ArrowLeft, 
    Calendar, 
    Music,
    Loader2,
    Play,
    Pause,
    Download,
    FileText
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const statusLabels = {
    new: 'New',
    reviewing: 'Under Review',
    shortlisted: 'Shortlisted',
    revisions_requested: 'Revisions Requested',
    ready_to_submit: 'Ready to Submit',
    submitted: 'Submitted',
    placed: 'Placed',
    rejected: 'Rejected'
};

const statusClasses = {
    new: 'status-new',
    reviewing: 'status-reviewing',
    shortlisted: 'status-shortlisted',
    revisions_requested: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    ready_to_submit: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    submitted: 'status-submitted',
    placed: 'status-placed',
    rejected: 'status-rejected'
};

const statusDescriptions = {
    new: 'Your submission has been received and is awaiting review.',
    reviewing: 'Our team is currently reviewing your track.',
    shortlisted: 'Great news! Your track has been shortlisted for consideration.',
    revisions_requested: 'We\'d like to request some changes to your submission.',
    ready_to_submit: 'Your track is ready to be submitted to the client.',
    submitted: 'Your track has been submitted to the client for final review.',
    placed: 'Congratulations! Your track has been placed!',
    rejected: 'Unfortunately, this submission was not selected.'
};

export default function SubmissionDetailPage() {
    const { submissionId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchSubmission();
    }, [submissionId]);

    const fetchSubmission = async () => {
        try {
            const response = await fetch(`${API}/submissions/${submissionId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubmission(data);
            } else {
                toast.error('Submission not found');
                navigate('/submissions');
            }
        } catch (error) {
            toast.error('Error loading submission');
        } finally {
            setLoading(false);
        }
    };

    const togglePlay = () => {
        if (audioRef.current) {
            if (playing) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setPlaying(!playing);
        }
    };

    const handleDownload = async () => {
        if (!submission?.id) return;
        
        // Use the streaming endpoint with token for download
        const downloadUrl = `${API}/stream/${submission.id}?token=${encodeURIComponent(token)}`;
        
        try {
            const response = await fetch(downloadUrl);
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = submission.track_title || submission.file_name || 'track.mp3';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
                toast.success('Download started');
            } else {
                toast.error('Failed to download file');
            }
        } catch (error) {
            toast.error('Error downloading file');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                </div>
            </Layout>
        );
    }

    if (!submission) return null;

    // Use streaming endpoint with token in URL for audio playback
    const audioUrl = `${API}/stream/${submission.id}?token=${encodeURIComponent(token)}`;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto" data-testid="submission-detail-page">
                {/* Back button */}
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/submissions')}
                    className="flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-6 transition-colors"
                    data-testid="back-btn"
                >
                    <ArrowLeft size={20} />
                    <span>Back to submissions</span>
                </motion.button>

                {/* Main content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                >
                    {/* Track info card */}
                    <div className="bg-[#262626] border border-[#404040] rounded-2xl p-6">
                        <div className="flex items-start gap-6">
                            {/* Album art placeholder */}
                            <div className="w-32 h-32 rounded-xl bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
                                <Music size={48} className="text-[#F51E1E]" />
                            </div>

                            {/* Info */}
                            <div className="flex-1">
                                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                                    {submission.track_title || 'Untitled Track'}
                                </h1>
                                <p className="text-[#A3A3A3] mb-4">
                                    Submitted for: {submission.sync_title || 'Unknown Opportunity'}
                                </p>

                                <div className="flex flex-wrap items-center gap-4">
                                    <Badge 
                                        variant="outline"
                                        className={`text-sm ${statusClasses[submission.status] || 'status-new'}`}
                                    >
                                        {statusLabels[submission.status] || submission.status}
                                    </Badge>
                                    <div className="flex items-center gap-1 text-sm text-[#A3A3A3]">
                                        <Calendar size={14} />
                                        <span>
                                            Submitted {new Date(submission.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Audio player */}
                        {submission.file_name && (
                            <div className="mt-6 pt-6 border-t border-[#404040]">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={togglePlay}
                                        data-testid="play-btn"
                                        className="w-14 h-14 rounded-full bg-[#F51E1E] hover:bg-[#D41212] flex items-center justify-center transition-colors"
                                    >
                                        {playing ? (
                                            <Pause size={24} className="text-white" />
                                        ) : (
                                            <Play size={24} className="text-white ml-1" />
                                        )}
                                    </button>

                                    <audio
                                        ref={audioRef}
                                        src={audioUrl}
                                        onEnded={() => setPlaying(false)}
                                        className="flex-1 h-10"
                                        controls
                                        style={{ 
                                            backgroundColor: '#1a1a1a',
                                            borderRadius: '8px'
                                        }}
                                    />

                                    <Button
                                        onClick={handleDownload}
                                        variant="outline"
                                        data-testid="download-btn"
                                        className="border-[#404040] hover:border-white text-white"
                                    >
                                        <Download size={18} className="mr-2" />
                                        Download
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status card */}
                    <div className="bg-[#262626] border border-[#404040] rounded-2xl p-6">
                        <h2 className="text-lg font-bold text-white mb-4">Status</h2>
                        <div className={`
                            p-4 rounded-xl border
                            ${statusClasses[submission.status] || 'status-new'}
                        `}>
                            <p className="font-medium mb-1">
                                {statusLabels[submission.status] || submission.status}
                            </p>
                            <p className="text-sm opacity-80">
                                {statusDescriptions[submission.status] || 'Status information pending.'}
                            </p>
                        </div>

                        {submission.status_updated_at && (
                            <p className="text-xs text-[#A3A3A3] mt-3">
                                Last updated: {new Date(submission.status_updated_at).toLocaleString()}
                            </p>
                        )}
                    </div>

                    {/* Notes card */}
                    {submission.notes && (
                        <div className="bg-[#262626] border border-[#404040] rounded-2xl p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <FileText size={20} className="text-[#A3A3A3]" />
                                <h2 className="text-lg font-bold text-white">Your Notes</h2>
                            </div>
                            <p className="text-[#A3A3A3] whitespace-pre-wrap">
                                {submission.notes}
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        </Layout>
    );
}
